/**
 * @see https://umijs.org/docs/max/access#access
 * 基于路径的自动权限匹配
 */
export default function access(
  initialState: { currentUser?: API.CurrentUser } | undefined,
) {
  const { currentUser } = initialState ?? {};

  // 判断是否是系统管理员（可管理所有租户）
  const isSystemAdmin = !!currentUser?.is_system_admin;

  // 判断是否是租户超级管理员
  const isSuperuser = !!currentUser?.is_superuser;

  // 判断是否是租户管理员（租户超级用户，但非系统管理员）
  const isTenantAdmin = isSuperuser && !isSystemAdmin;

  // 判断是否是管理员：系统管理员、租户超级管理员 或 拥有管理员角色
  const isAdmin =
    isSystemAdmin ||
    isSuperuser ||
    currentUser?.roles?.some((role) =>
      ['admin', 'administrator', '管理员'].includes(role.toLowerCase?.() || role)
    );

  // 获取用户权限代码列表
  const permissionCodes = currentUser?.permissions?.map((p) => p.code) || [];

  // 获取用户所有菜单权限的路径列表（确保 path 是字符串）
  const menuPaths = currentUser?.permissions
    ?.filter((p) => p.type === 'menu' && p.path && typeof p.path === 'string')
    ?.map((p) => p.path as string) || [];

  // 获取包含子路径权限的路径列表（确保 path 是字符串）
  const pathsWithSubPaths = currentUser?.permissions
    ?.filter((p) => p.type === 'menu' && p.path && typeof p.path === 'string' && p.include_sub_paths === true)
    ?.map((p) => p.path as string) || [];

  // 检查是否有某个权限（用于按钮级权限）
  const hasPermission = (code: string): boolean => {
    // 系统管理员和租户超级用户拥有所有权限
    if (isSystemAdmin || isSuperuser) return true;
    return permissionCodes.includes(code);
  };

  // 通用路由权限检查（核心）
  const canAccessRoute = (path: string): boolean => {
    if (!currentUser) return false;
    // 系统管理员和租户超级用户可访问所有路由
    if (isSystemAdmin || isSuperuser) return true;
    // 防御性检查：确保 path 是字符串（超级用户已提前返回）
    if (!path || typeof path !== 'string') return false;

    // 精确匹配
    if (menuPaths.includes(path)) return true;

    // 检查是否有子路由权限（当前路径是某个有权限路径的父路径）
    // 例如：用户有 /system/user 权限，那么 /system 父菜单也应该显示
    if (menuPaths.some((menuPath) => menuPath.startsWith(path + '/'))) return true;

    // 新增：检查父路径的 include_sub_paths 权限
    // 如果父路径配置了 include_sub_paths=true，则可以访问其子路径
    // 例如：用户有 /sales/contract 且 include_sub_paths=true
    //      可以访问 /sales/contract/5, /sales/contract/create 等
    for (const parentPath of pathsWithSubPaths) {
      if (path.startsWith(parentPath + '/')) {
        return true;
      }
    }

    return false;
  };

  return {
    // 通用权限检查函数
    canAccessRoute,
    hasPermission,
    // 保留管理员判断（用于系统管理模块入口等特殊场景）
    canAdmin: !!currentUser && isAdmin,
    // 多租户相关权限
    isSystemAdmin,           // 系统管理员（可管理所有租户）
    isTenantAdmin,           // 租户管理员（管理本租户）
    canManageTenants: isSystemAdmin, // 是否可管理租户
  };
}
